import { useEffect, useRef, useState } from 'react';
import workflowService from '../../services/workflow.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { colors } from '../../styles/theme';
import { useMovablePopup } from '../MovablePopup';

interface Props {
  caseId: string;
  /** event_trigger sent to /workflow/cases/{id}/transition. Defaults
   *  to the contract-amendment event for backwards compatibility;
   *  the KYC `REQUEST_INFO` flow passes its own event. */
  event?: string;
  /** Modal title shown at the top. Defaults to "Return to member". */
  title?: string;
  /** One-line intro under the title. Pass workflow-specific copy
   *  when the default isn't a fit (e.g. KYC info request). */
  description?: string;
  /** Submit button label. Defaults to "Return to member". */
  submitLabel?: string;
  /** Optional placeholder shown inside the summary textarea — useful
   *  for steering the agent toward the right voice per workflow. */
  summaryPlaceholder?: string;
  /** Optional flash message shown after submit; passed through to
   *  the caller's onSubmitted callback as the first arg if needed.
   *  (Not used by this component directly — kept as a hint.) */
  onClose: () => void;
  onSubmitted: () => void;
}

const MIN_SUMMARY = 10;
const MAX_SUMMARY = 500;
const MAX_NOTES = 1000;
const MIN_ISSUE = 5;
const MAX_ISSUE = 300;
const MAX_ISSUES = 10;

/**
 * Modal for `agent:return_to_client`. Two thoughtfully-separated inputs:
 *
 *   1. Internal notes (optional, staff-only). Goes to
 *      `state_transitions_log.notes`. Never surfaces to the client.
 *
 *   2. Client-visible message (required, structured). Shape matches the
 *      backend's ClientVisibleMessage schema:
 *        { summary: string, issues: string[1..10] }
 *      The client UI renders this verbatim as a yellow banner with a
 *      bulleted list — see ClientMessageBanner. The structured shape is
 *      deliberately narrow to prevent staff from leaking internal review
 *      thread or reviewer identity through free-form text.
 *
 * The visual divider in the form mirrors what the client will see, so
 * staff can sanity-check "is this ok to share" before submitting.
 */
export default function ReturnToClientModal({
  caseId,
  event = 'agent:return_to_client',
  title = 'Return to member',
  description = "The case goes back to the member with your curated message. They will see a bullet-list of issues to fix — internal notes stay staff-only.",
  submitLabel = 'Return to member',
  summaryPlaceholder = 'Your contract requires a few corrections before we can approve it.',
  onClose,
  onSubmitted,
}: Readonly<Props>): JSX.Element {
  const popup = useMovablePopup<HTMLDialogElement>();
  const [internalNotes, setInternalNotes] = useState<string>('');
  const [summary, setSummary] = useState<string>('');
  const issueKeySeq = useRef(0);
  const nextIssueKey = (): string => {
    issueKeySeq.current += 1;
    return `issue-${issueKeySeq.current}`;
  };
  const [issues, setIssues] = useState<{ key: string; value: string }[]>(
    () => [{ key: nextIssueKey(), value: '' }],
  );
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape' && !submitting) onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, submitting]);

  function setIssueAt(idx: number, value: string): void {
    setIssues((prev) => prev.map((it, i) => (i === idx ? { ...it, value } : it)));
  }

  function addIssue(): void {
    if (issues.length >= MAX_ISSUES) return;
    setIssues((prev) => [...prev, { key: nextIssueKey(), value: '' }]);
  }

  function removeIssueAt(idx: number): void {
    if (issues.length <= 1) return;
    setIssues((prev) => prev.filter((_, i) => i !== idx));
  }

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);

    const trimmedSummary = summary.trim();
    if (trimmedSummary.length < MIN_SUMMARY) {
      setError(`Summary must be at least ${MIN_SUMMARY} characters.`);
      return;
    }
    if (trimmedSummary.length > MAX_SUMMARY) {
      setError(`Summary must be at most ${MAX_SUMMARY} characters.`);
      return;
    }

    const trimmedIssues = issues.map((i) => i.value.trim()).filter((i) => i.length > 0);
    if (trimmedIssues.length === 0) {
      setError('Please add at least one issue.');
      return;
    }
    for (const iss of trimmedIssues) {
      if (iss.length < MIN_ISSUE || iss.length > MAX_ISSUE) {
        setError(`Each issue must be ${MIN_ISSUE}–${MAX_ISSUE} characters.`);
        return;
      }
    }

    const trimmedNotes = internalNotes.trim();
    setSubmitting(true);
    try {
      await workflowService.applyTransition(caseId, {
        event_trigger: event,
        notes: trimmedNotes || null,
        client_visible_notes: {
          summary: trimmedSummary,
          issues: trimmedIssues,
        },
      });
      onSubmitted();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Could not return this case to the member.'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(6,9,26,0.55)' }}
    >
      <dialog
        ref={popup.panelRef}
        open
        aria-modal="true"
        aria-labelledby="return-to-client-modal-title"
        className="w-full rounded-2xl p-6 overflow-y-auto"
        style={{
          ...popup.panelStyle,
          background: colors.surface,
          maxHeight: '90vh',
          margin: 0,
          border: 'none',
          maxWidth: '42rem',
          color: 'inherit',
        }}
        onCancel={(e) => { if (submitting) e.preventDefault(); else onClose(); }}
      >
        {popup.bar}
        <h2
          id="return-to-client-modal-title"
          className="text-lg font-semibold mb-1"
          style={{ color: colors.ink }}
        >
          {title}
        </h2>
        <p className="text-sm mb-5" style={{ color: colors.muted }}>
          {description}
        </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Internal notes — staff-only */}
          <div>
            <label
              htmlFor="return-to-client-internal-notes"
              className="block text-xs font-bold uppercase mb-2"
              style={{ color: colors.muted }}
            >
              Internal notes (staff-only)
            </label>
            <div className="block text-sm">
              <textarea
                id="return-to-client-internal-notes"
                value={internalNotes}
                onChange={(e) => setInternalNotes(e.target.value)}
                rows={3}
                maxLength={MAX_NOTES}
                className="w-full px-3 py-2 rounded-lg text-sm"
                style={{ border: `1px solid ${colors.border}`, color: colors.ink }}
                placeholder="Optional. Anything you want the manager / future reviewers to know — never seen by the member."
              />
            </div>
          </div>

          {/* Visual divider */}
          <div className="border-t pt-5" style={{ borderColor: colors.border }}>
            <h3 className="text-xs font-bold uppercase mb-1" style={{ color: colors.warning }}>
              Visible to the member
            </h3>
            <p className="text-xs mb-3" style={{ color: colors.muted }}>
              The summary and bullets below appear verbatim on the member&apos;s
              contract page. Avoid internal jargon, reviewer names, or
              speculation about cause.
            </p>

            <label className="block text-sm mb-3">
              <span className="block mb-1 font-medium" style={{ color: colors.ink }}>
                Summary{' '}
                <span className="font-normal" style={{ color: colors.mutedSoft }}>
                  ({MIN_SUMMARY}–{MAX_SUMMARY} chars)
                </span>
              </span>
              <textarea
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                rows={2}
                minLength={MIN_SUMMARY}
                maxLength={MAX_SUMMARY}
                className="w-full px-3 py-2 rounded-lg text-sm"
                style={{ border: `1px solid ${colors.border}`, color: colors.ink }}
                placeholder={summaryPlaceholder}
              />
            </label>

            <div className="text-sm">
              <span className="block mb-1 font-medium" style={{ color: colors.ink }}>
                Issues to address{' '}
                <span className="font-normal" style={{ color: colors.mutedSoft }}>
                  ({issues.length} of {MAX_ISSUES} max)
                </span>
              </span>
              <div className="space-y-2">
                {issues.map((issue, idx) => (
                  <div key={issue.key} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={issue.value}
                      onChange={(e) => setIssueAt(idx, e.target.value)}
                      maxLength={MAX_ISSUE}
                      className="flex-1 px-3 py-2 rounded-lg text-sm"
                      style={{ border: `1px solid ${colors.border}`, color: colors.ink }}
                      placeholder={`Issue ${idx + 1} — short, specific, actionable`}
                    />
                    <button
                      type="button"
                      onClick={() => removeIssueAt(idx)}
                      disabled={issues.length <= 1}
                      title="Remove issue"
                      aria-label="Remove issue"
                      className="px-2.5 py-2 rounded-lg text-sm flex-shrink-0"
                      style={{
                        background: colors.surface,
                        color: colors.danger,
                        border: `1px solid ${colors.border}`,
                        opacity: issues.length <= 1 ? 0.4 : 1,
                        cursor: issues.length <= 1 ? 'not-allowed' : 'pointer',
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={addIssue}
                disabled={issues.length >= MAX_ISSUES}
                className="mt-2 px-3 py-1.5 rounded-lg text-xs font-medium"
                style={{
                  background: colors.surface,
                  color: colors.ink,
                  border: `1px solid ${colors.border}`,
                  opacity: issues.length >= MAX_ISSUES ? 0.4 : 1,
                  cursor: issues.length >= MAX_ISSUES ? 'not-allowed' : 'pointer',
                }}
              >
                + Add issue
              </button>
            </div>
          </div>

          {error && (
            <div
              className="rounded-lg p-3 text-sm"
              style={{
                background: colors.dangerBg,
                color: colors.danger,
                border: `1px solid ${colors.dangerBorder}`,
              }}
              role="alert"
            >
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              style={{
                background: colors.surface,
                color: colors.inkSoft,
                border: `1px solid ${colors.border}`,
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
              style={{
                background: colors.navy,
                color: '#FFFFFF',
                opacity: submitting ? 0.6 : 1,
                cursor: submitting ? 'wait' : 'pointer',
              }}
            >
              {submitting ? 'Sending…' : submitLabel}
            </button>
          </div>
        </form>
      </dialog>
    </div>
  );
}
